import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Code,
  Divider,
  Heading,
  Badge,
  Textarea,
} from '@chakra-ui/react';
import { authApi } from '../services/api';

export const ApiTest: React.FC = () => {
  const [email, setEmail] = useState('test@redeuce.com');
  const [password, setPassword] = useState('TestPassword123!');
  const [firstName, setFirstName] = useState('Test');
  const [lastName, setLastName] = useState('User');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const testRegistration = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authApi.register({
        email,
        password,
        firstName,
        lastName,
      });
      setResult(response);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const testLogin = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authApi.login({
        email,
        password,
      });
      setResult(response);
      
      // Store tokens for future requests
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setResult(null);
    setError(null);
  };

  const formatTokenPreview = (token: string) => {
    if (!token) return '';
    return token.substring(0, 50) + '...';
  };

  return (
    <Box p={6} maxW="1000px" mx="auto">
      <Heading size="lg" mb={6}>🔗 API Integration Test</Heading>
      
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel>Password</FormLabel>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormControl>

        <HStack spacing={2}>
          <FormControl>
            <FormLabel>First Name</FormLabel>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </FormControl>

          <FormControl>
            <FormLabel>Last Name</FormLabel>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </FormControl>
        </HStack>

        <HStack spacing={4}>
          <Button 
            colorScheme="blue" 
            onClick={testRegistration} 
            isLoading={loading}
            loadingText="Registering..."
          >
            Test Registration
          </Button>
          
          <Button 
            colorScheme="green" 
            onClick={testLogin} 
            isLoading={loading}
            loadingText="Logging in..."
          >
            Test Login
          </Button>

          <Button variant="outline" onClick={clearResults}>
            Clear Results
          </Button>
        </HStack>

        <Divider />

        {error && (
          <Alert status="error">
            <AlertIcon />
            <Text>{error}</Text>
          </Alert>
        )}

        {result && (
          <Alert status="success">
            <AlertIcon />
            <VStack align="start" spacing={4} w="100%">
              <Text fontWeight="bold">✅ API Response Successful!</Text>
              
              {result.user && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>User Information:</Text>
                  <HStack spacing={2} wrap="wrap">
                    <Badge colorScheme="blue">ID: {result.user.userId}</Badge>
                    <Badge colorScheme="green">Email: {result.user.email}</Badge>
                    <Badge colorScheme="purple">Name: {result.user.firstName} {result.user.lastName}</Badge>
                    {result.user.isVerified && <Badge colorScheme="green">✓ Verified</Badge>}
                  </HStack>
                </Box>
              )}

              {result.accessToken && (
                <Box w="100%">
                  <Text fontWeight="semibold" mb={2}>Authentication Tokens:</Text>
                  <VStack spacing={2} align="start">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">Access Token:</Text>
                      <Code fontSize="xs" p={2} borderRadius="md" w="100%">
                        {formatTokenPreview(result.accessToken)}
                      </Code>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">Refresh Token:</Text>
                      <Code fontSize="xs" p={2} borderRadius="md" w="100%">
                        {formatTokenPreview(result.refreshToken)}
                      </Code>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">ID Token:</Text>
                      <Code fontSize="xs" p={2} borderRadius="md" w="100%">
                        {formatTokenPreview(result.idToken)}
                      </Code>
                    </Box>
                    <Badge colorScheme="orange">Expires in: {result.expiresIn} seconds</Badge>
                  </VStack>
                </Box>
              )}

              <Box w="100%">
                <Text fontWeight="semibold" mb={2}>Full Response:</Text>
                <Textarea 
                  value={JSON.stringify(result, null, 2)} 
                  readOnly 
                  h="200px" 
                  fontSize="xs"
                  fontFamily="mono"
                />
              </Box>
            </VStack>
          </Alert>
        )}

        <Box>
          <Text fontSize="sm" color="gray.600">
            <strong>API Base URL:</strong> {import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev'}
          </Text>
          <Text fontSize="sm" color="gray.600">
            <strong>Environment:</strong> {import.meta.env.VITE_USE_MOCK_DATA === 'true' ? 'Mock Data' : 'Live API'}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}; 